import { useEffect, useState } from "react";
import { Mail, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, LoadingState, ErrorState, Card, SectionTitle, Field, FormError, SubmitButton, Badge, ConfirmDialog } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/lib/useFetch";
import { api, errMsg } from "@/lib/api";
import { t } from "@/i18n";

const TEXT = ["business_name", "legal_name", "abn", "address", "support_email", "phone", "website"];
const LABEL = { business_name: "businessName", legal_name: "legalName", abn: "abn", address: "address", support_email: "supportEmail", phone: "phone", website: "website" };

function PlatformForm({ initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => setF(initial), [initial]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const payload = { ...f, support_email: f.support_email || null, abn: (f.abn || "").replace(/\s/g, ""), default_trial_days: Number(f.default_trial_days) };
      onSaved((await api.put("/admin/platform", payload)).data);
      toast.success(t("p7.platform.saved"));
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="platform-form">
      <FormError message={error} />
      <div className="grid gap-4 sm:grid-cols-2">
        {TEXT.map((k) => (
          <Field key={k} label={t(`p7.platform.${LABEL[k]}`)} htmlFor={`pf-${k}`}>
            <Input id={`pf-${k}`} value={f[k] || ""} required={k === "business_name"} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`platform-${k}-input`} />
          </Field>
        ))}
        <Field label={t("p7.platform.defaultTrial")} htmlFor="pf-trial"><Input id="pf-trial" type="number" min={0} max={365} value={f.default_trial_days ?? 14} onChange={(e) => setF({ ...f, default_trial_days: e.target.value })} data-testid="platform-trial-input" /></Field>
      </div>
      <label className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4 text-sm">
        <span><span className="block font-semibold text-slate-900">{t("p7.platform.gst")}</span><span className="text-slate-600">{t("p7.platform.gstHint")}</span></span>
        <Switch checked={!!f.gst_registered} onCheckedChange={(v) => setF({ ...f, gst_registered: v })} data-testid="platform-gst-switch" />
      </label>
      <label className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 p-4 text-sm">
        <span><span className="block font-semibold text-slate-900">{t("p7.approval.setting")}</span><span className="text-slate-600">{t("p7.approval.settingHint")}</span></span>
        <Switch checked={f.signup_requires_approval !== false} onCheckedChange={(v) => setF({ ...f, signup_requires_approval: v })} data-testid="platform-approval-switch" />
      </label>
      <SubmitButton busy={busy} data-testid="platform-save-button">{t("common.save")}</SubmitButton>
    </form>
  );
}

function DemoModeCard({ on, onChanged }) {
  const [confirm, setConfirm] = useState(false);
  const { refresh } = useAuth();
  const toggle = async () => {
    try {
      await api.post("/admin/demo-mode", { demo_mode: !on });
      toast.success(t(on ? "b8.demo.off" : "b8.demo.on"));
      setConfirm(false);
      onChanged();
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card testId="demo-mode-card">
      <SectionTitle>{t("common.demoMode")}</SectionTitle>
      <label className="flex items-start justify-between gap-3 text-sm"><span className="text-slate-600">{t("b8.demo.hint")}</span>
        <Switch checked={on} onCheckedChange={() => setConfirm(true)} data-testid="platform-demo-switch" /></label>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} destructive={on} title={t(on ? "b8.demo.offTitle" : "b8.demo.onTitle")} description={t(on ? "b8.demo.offText" : "b8.demo.onText")}
        confirmLabel={t("common.confirm")} onConfirm={toggle} testId="demo-mode-dialog" />
    </Card>
  );
}

export default function AdminPlatform() {
  const { data, error, loading, reload, setData } = useFetch("/admin/platform");
  const stats = useFetch("/admin/stats");
  if (loading && !data) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  return (
    <div>
      <PageHeader eyebrow={t("admin.area")} title={t("p7.platform.title")} subtitle={t("p7.platform.subtitle")} />
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card><PlatformForm initial={data} onSaved={setData} /></Card>
        <div className="space-y-6">
          <DemoModeCard on={!!data.demo_mode} onChanged={reload} />
          <Card testId="platform-email-card">
            <SectionTitle><Mail className="mr-2 inline h-4 w-4" />{t("p7.platform.email")}</SectionTitle>
            <p className="text-sm text-slate-600" data-testid="platform-email-status">{stats.data?.email_provider ? t("p7.platform.emailOn") : t("p7.platform.emailOff")}</p>
          </Card>
          <Card testId="platform-stripe-card">
            <SectionTitle><CreditCard className="mr-2 inline h-4 w-4" />{t("p7.platform.stripe")}</SectionTitle>
            <Badge tone="amber">{t("p7.platform.stripeTest")}</Badge>
            <p className="mt-3 text-sm text-slate-600">{t("p7.platform.stripeHint")}</p>
          </Card>
        </div>
      </div>
    </div>
  );
}
